import React, { useEffect, useState } from 'react';
import DashboardLayout from '../../../components/layout/dashboard';
import { useTitle } from '../../../hooks/useTitle';
import { useParams } from 'react-router-dom';
import { axiosInstance } from '../../../utils/axios';
import {
  Button,
  Card,
  Collapse,
  Form,
  Input,
  Modal,
  notification,
  Switch,
} from 'antd';
import { validateEmail } from '../../../utils/validators';
import { ArrowRightOutlined } from '@ant-design/icons';
import LoadingSpinner from '../../../components/screening-steps/util/LoadingSpinner';

interface Company {
  id: string;
  name: string;
  credits: number;
  kvkNumber: string;
  phoneNumber: string;
  email: string;
  expertInterpretationEmail: string;
  screeningStepsConfig: {
    identification: boolean;
    bankData: boolean;
    incomeCheck: boolean;
    kvk: boolean;
    bkr: boolean;
    kadaster: boolean;
    landlordStatement: boolean;
    employeeIntegrity: boolean;
    education: boolean;
    certificateOfConduct: boolean;
  };
}

const { confirm } = Modal;
const { Panel } = Collapse;
const EditCompany = () => {
  useTitle('Bedrijven');
  const { id } = useParams();
  const [form] = Form.useForm();
  const [company, setCompany] = useState<Company | null>(null);
  const [messageApi, contextHolder] = notification.useNotification();

  const [loadingData, setLoadingData] = useState(true);

  useEffect(() => {
    axiosInstance
      .get(`/company/info/${id}`)
      .then((res) => {
        form.setFieldsValue(res.data);
        setCompany(res.data);
      })
      .catch((err) => {
        messageApi.error({
          message:
            'Er is iets misgegaan bij het ophalen van de bedrijfsgegevens',
          placement: 'bottomRight',
        });
      })
      .finally(() => setLoadingData(false));
  }, [form, id, messageApi]);

  function differenceComponentBuilder(a: string, b: string, name: string) {
    return (
      <p>
        <span style={{ display: 'block' }}>{name}</span>{' '}
        <span style={{ textDecoration: 'line-through' }}>{a}</span>{' '}
        <ArrowRightOutlined /> <strong>{b}</strong>
      </p>
    );
  }

  function differenceComponent(a: Company | null, bank: Company | null) {
    return (
      a &&
      bank && (
        <div>
          {a.name !== bank.name &&
            differenceComponentBuilder(a.name, bank.name, 'Bedrijfsnaam')}
          {a.kvkNumber !== bank.kvkNumber &&
            differenceComponentBuilder(
              a.kvkNumber,
              bank.kvkNumber,
              'KVK nummer',
            )}
          {a.email !== bank.email &&
            differenceComponentBuilder(a.email, bank.email, 'Email')}
          {a.expertInterpretationEmail !== bank.expertInterpretationEmail &&
            differenceComponentBuilder(
              a.expertInterpretationEmail,
              bank.expertInterpretationEmail,
              "Duiding's email",
            )}
          {a.screeningStepsConfig.identification !==
            bank.screeningStepsConfig.identification &&
            differenceComponentBuilder(
              a.screeningStepsConfig.identification ? 'Ja' : 'Nee',
              bank.screeningStepsConfig.identification ? 'Ja' : 'Nee',
              'ID Check',
            )}
          {a.screeningStepsConfig.bankData !==
            bank.screeningStepsConfig.bankData &&
            differenceComponentBuilder(
              a.screeningStepsConfig.bankData ? 'Ja' : 'Nee',
              bank.screeningStepsConfig.bankData ? 'Ja' : 'Nee',
              'Bank verificatie',
            )}
          {a.screeningStepsConfig.incomeCheck !==
            bank.screeningStepsConfig.incomeCheck &&
            differenceComponentBuilder(
              a.screeningStepsConfig.incomeCheck ? 'Ja' : 'Nee',
              bank.screeningStepsConfig.incomeCheck ? 'Ja' : 'Nee',
              'Inkomenscheck',
            )}
          {a.screeningStepsConfig.kvk !== bank.screeningStepsConfig.kvk &&
            differenceComponentBuilder(
              a.screeningStepsConfig.kvk ? 'Ja' : 'Nee',
              bank.screeningStepsConfig.kvk ? 'Ja' : 'Nee',
              'KVK Check',
            )}
          {a.screeningStepsConfig.bkr !== bank.screeningStepsConfig.bkr &&
            differenceComponentBuilder(
              a.screeningStepsConfig.bkr ? 'Ja' : 'Nee',
              bank.screeningStepsConfig.bkr ? 'Ja' : 'Nee',
              'BKR Check',
            )}
          {a.screeningStepsConfig.kadaster !==
            bank.screeningStepsConfig.kadaster &&
            differenceComponentBuilder(
              a.screeningStepsConfig.kadaster ? 'Ja' : 'Nee',
              bank.screeningStepsConfig.kadaster ? 'Ja' : 'Nee',
              'Kadaster',
            )}
          {a.screeningStepsConfig.landlordStatement !==
            bank.screeningStepsConfig.landlordStatement &&
            differenceComponentBuilder(
              a.screeningStepsConfig.landlordStatement ? 'Ja' : 'Nee',
              bank.screeningStepsConfig.landlordStatement ? 'Ja' : 'Nee',
              'Referentiecheck - Huurder',
            )}
          {a.screeningStepsConfig.employeeIntegrity !==
            bank.screeningStepsConfig.employeeIntegrity &&
            differenceComponentBuilder(
              a.screeningStepsConfig.employeeIntegrity ? 'Ja' : 'Nee',
              bank.screeningStepsConfig.employeeIntegrity ? 'Ja' : 'Nee',
              'Referentiecheck - Medewerkers',
            )}
          {a.screeningStepsConfig.education !==
            bank.screeningStepsConfig.education &&
            differenceComponentBuilder(
              a.screeningStepsConfig.education ? 'Ja' : 'Nee',
              bank.screeningStepsConfig.education ? 'Ja' : 'Nee',
              'Opleidingscheck',
            )}
          {a.screeningStepsConfig.certificateOfConduct !==
            bank.screeningStepsConfig.certificateOfConduct &&
            differenceComponentBuilder(
              a.screeningStepsConfig.certificateOfConduct ? 'Ja' : 'Nee',
              bank.screeningStepsConfig.certificateOfConduct ? 'Ja' : 'Nee',
              'Verklaring Omtrent Gedrag',
            )}
        </div>
      )
    );
  }

  function submitForm(values: Company) {
    confirm({
      title: 'Weet je zeker dat je deze wijzigingen wilt opslaan?',
      content: differenceComponent(company, values),
      onOk() {
        axiosInstance
          .put(`/company/update/${id}`, values)
          .then(() => {
            Modal.success({
              content: 'Bedrijf succesvol aangepast',
            });
          })
          .catch(() => {
            messageApi.error({
              message: 'Er is iets misgegaan, probeer het later nog eens',
              placement: 'bottomRight',
            });
          });
      },
      width: 800,
    });
  }
  return (
    <DashboardLayout>
      {contextHolder}
      {loadingData ? (
        <LoadingSpinner />
      ) : (
        <Card title={`${company?.name} bewerken`} style={{ marginTop: 20 }}>
          <Form layout="vertical" form={form} onFinish={submitForm}>
            <Form.Item label="Bedrijfsnaam" name="name">
              <Input maxLength={2000} />
            </Form.Item>
            <Form.Item label="KVK nummer" name="kvkNumber" required>
              <Input maxLength={20} />
            </Form.Item>
            <Form.Item label="Telefoon nummer" name="phoneNumber" required>
              <Input maxLength={50} />
            </Form.Item>
            <Form.Item label="Email" name="email" required>
              <Input maxLength={2000} />
            </Form.Item>
            <Form.Item
              label="Email voor expert duiding"
              name="expertInterpretationEmail"
              rules={[{ validator: validateEmail }]}
              required
            >
              <Input type="email" maxLength={2000} />
            </Form.Item>
            <Collapse ghost>
              <Panel header="Toegestane checks" key="allowedChecks">
                <Form.Item
                  label="ID Check"
                  name={['screeningStepsConfig', 'identification']}
                  required
                >
                  <Switch />
                </Form.Item>
                <Form.Item
                  label="Bank verificatie"
                  name={['screeningStepsConfig', 'bankData']}
                  required
                >
                  <Switch />
                </Form.Item>
                <Form.Item
                  label="Inkomenscheck"
                  name={['screeningStepsConfig', 'incomeCheck']}
                  required
                >
                  <Switch />
                </Form.Item>
                <Form.Item
                  label="KVK Check"
                  name={['screeningStepsConfig', 'kvk']}
                  required
                >
                  <Switch />
                </Form.Item>
                <Form.Item
                  label="BKR Check"
                  name={['screeningStepsConfig', 'bkr']}
                  required
                >
                  <Switch />
                </Form.Item>
                <Form.Item
                  label="Kadaster"
                  name={['screeningStepsConfig', 'kadaster']}
                  required
                >
                  <Switch />
                </Form.Item>
                <Form.Item
                  label="Referentiecheck - Huurder"
                  name={['screeningStepsConfig', 'landlordStatement']}
                  required
                >
                  <Switch />
                </Form.Item>
                <Form.Item
                  label="Referentiecheck - Medewerkers"
                  name={['screeningStepsConfig', 'employeeIntegrity']}
                  required
                >
                  <Switch />
                </Form.Item>
                <Form.Item
                  label="Opleidingscheck"
                  name={['screeningStepsConfig', 'education']}
                  required
                >
                  <Switch />
                </Form.Item>
                <Form.Item
                  label="Verklaring Omtrent Gedrag"
                  name={['screeningStepsConfig', 'certificateOfConduct']}
                  required
                >
                  <Switch />
                </Form.Item>
              </Panel>
            </Collapse>
            <Form.Item>
              <Button type="primary" htmlType="submit">
                Opslaan
              </Button>
            </Form.Item>
          </Form>
        </Card>
      )}
    </DashboardLayout>
  );
};

export default EditCompany;
